import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  X,
  Calendar,
  Package,
  MapPin,
  DollarSign,
  Truck,
  Plus,
  Edit,
  Trash2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Label } from './ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Separator } from './ui/separator';
import { orderManager, formatCurrency, formatDate, formatQuantity, formatWeight } from '../mock';
import { useToast } from '../hooks/use-toast';

const EditOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [editDispatchId, setEditDispatchId] = useState(null);
  const [formData, setFormData] = useState({
    orderDate: '',
    brandName: '',
    poNumber: '',
    billTo: '',
    shipTo: '',
    materialType: '',
    quantity: '',
    unit: 'Nos',
    price: ''
  });
  const [dispatchForm, setDispatchForm] = useState({
    dispatchDate: new Date().toISOString().split('T')[0],
    dispatchNumber: '',
    quantity: '',
    weightInKgs: ''
  });

  useEffect(() => {
    const orderData = orderManager.getOrderById(id);
    if (orderData) {
      setOrder(orderData);
      setFormData({
        orderDate: orderData.orderDate,
        brandName: orderData.brandName,
        poNumber: orderData.poNumber,
        billTo: orderData.billTo,
        shipTo: orderData.shipTo,
        materialType: orderData.materialType,
        quantity: orderData.quantity.toString(),
        unit: orderData.unit,
        price: orderData.price.toString()
      });
    } else {
      toast({
        title: 'Order Not Found',
        description: 'The requested order could not be found.',
        variant: 'destructive'
      });
      navigate('/orders');
    }
    setLoading(false);
  }, [id, navigate, toast]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSameBillToShipTo = () => {
    setFormData(prev => ({ ...prev, shipTo: prev.billTo }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.orderDate) errors.orderDate = 'Order date is required';
    if (!formData.brandName.trim()) errors.brandName = 'Brand name is required';
    if (!formData.poNumber.trim()) errors.poNumber = 'PO number is required';
    if (!formData.billTo.trim()) errors.billTo = 'Bill to address is required';
    if (!formData.shipTo.trim()) errors.shipTo = 'Ship to address is required';
    if (!formData.materialType.trim()) errors.materialType = 'Material type is required';
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      errors.quantity = 'Valid quantity is required';
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      errors.price = 'Valid price is required';
    }
    return errors;
  };

  const handleSaveOrder = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedData = {
        ...formData,
        quantity: parseFloat(formData.quantity),
        price: parseFloat(formData.price)
      };

      const updatedOrder = orderManager.updateOrder(order.id, updatedData);
      if (updatedOrder) {
        setOrder(updatedOrder);
        toast({
          title: 'Order Updated',
          description: 'Order has been updated successfully.',
        });
      } else {
        throw new Error('Failed to update order');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update order. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddDispatch = () => {
    if (!dispatchForm.dispatchNumber.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Dispatch number is required.',
        variant: 'destructive'
      });
      return;
    }

    const quantity = parseFloat(dispatchForm.quantity);
    const weightInKgs = parseFloat(dispatchForm.weightInKgs);
    
    if (!quantity || quantity <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Valid quantity is required.',
        variant: 'destructive'
      });
      return;
    }

    if (!weightInKgs || weightInKgs <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Weight in Kgs must be greater than 0.',
        variant: 'destructive'
      });
      return;
    }

    if (quantity > order.balance) {
      toast({
        title: 'Validation Error',
        description: `Dispatch quantity cannot exceed balance (${formatQuantity(order.balance, order.unit)}).`,
        variant: 'destructive'
      });
      return;
    }

    const dispatchData = {
      ...dispatchForm,
      quantity,
      weightInKgs,
      unit: order.unit
    };

    const updatedOrder = orderManager.addDispatch(order.id, dispatchData);
    if (updatedOrder) {
      setOrder(updatedOrder);
      setDispatchDialogOpen(false);
      setDispatchForm({
        dispatchDate: new Date().toISOString().split('T')[0],
        dispatchNumber: '',
        quantity: '',
        weightInKgs: ''
      });

      toast({
        title: 'Dispatch Added',
        description: `Dispatch ${dispatchForm.dispatchNumber} added successfully. Weight: ${formatWeight(weightInKgs)}`,
      });
    }
  };

  const handleDeleteDispatch = (dispatchId) => {
    if (window.confirm('Are you sure you want to delete this dispatch?')) {
      // Remove dispatch from the order
      const updatedOrder = { ...order };
      updatedOrder.dispatches = updatedOrder.dispatches.filter(d => d.id !== dispatchId);
      
      // Recalculate balance
      const totalDispatched = updatedOrder.dispatches.reduce((sum, dispatch) => sum + dispatch.quantity, 0);
      updatedOrder.balance = updatedOrder.quantity - totalDispatched;
      updatedOrder.status = updatedOrder.balance === 0 ? 'Complete' : 'Pending';
      
      orderManager.updateOrder(order.id, updatedOrder);
      setOrder(updatedOrder);
      
      toast({
        title: 'Dispatch Deleted',
        description: 'Dispatch has been deleted successfully.',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Order Not Found</h3>
          <p className="text-gray-600 mb-4">The requested order could not be found.</p>
          <Link to="/orders">
            <Button>Back to Orders</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link to="/orders">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Orders
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Order</h1>
              <p className="text-gray-600">{order.brandName} - {order.poNumber}</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={() => navigate('/orders')}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button 
              onClick={handleSaveOrder}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </div>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="orderDate">Order Date *</Label>
                    <Input
                      id="orderDate"
                      type="date"
                      value={formData.orderDate}
                      onChange={(e) => handleInputChange('orderDate', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="poNumber">PO Number *</Label>
                    <Input
                      id="poNumber"
                      type="text"
                      placeholder="e.g., PO-2024-001"
                      value={formData.poNumber}
                      onChange={(e) => handleInputChange('poNumber', e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="brandName">Brand Name *</Label>
                  <Input
                    id="brandName"
                    type="text"
                    placeholder="Enter brand or company name"
                    value={formData.brandName}
                    onChange={(e) => handleInputChange('brandName', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Addresses */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  Addresses
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="billTo">Bill To Address *</Label>
                  <Textarea
                    id="billTo"
                    placeholder="Complete billing address with city, state, pincode"
                    value={formData.billTo}
                    onChange={(e) => handleInputChange('billTo', e.target.value)}
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="shipTo">Ship To Address *</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSameBillToShipTo}
                    >
                      Same as Bill To
                    </Button>
                  </div>
                  <Textarea
                    id="shipTo"
                    placeholder="Complete shipping address with city, state, pincode"
                    value={formData.shipTo}
                    onChange={(e) => handleInputChange('shipTo', e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Product Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Package className="w-5 h-5 mr-2" />
                  Product Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="materialType">Type of Material *</Label>
                  <Input
                    id="materialType"
                    type="text"
                    placeholder="e.g., Plastic Bags, Steel Sheets, Aluminum Rods"
                    value={formData.materialType}
                    onChange={(e) => handleInputChange('materialType', e.target.value)}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantity *</Label>
                    <Input
                      id="quantity"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Enter quantity"
                      value={formData.quantity}
                      onChange={(e) => handleInputChange('quantity', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="unit">Unit *</Label>
                    <Select value={formData.unit} onValueChange={(value) => handleInputChange('unit', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Nos">Nos (Numbers)</SelectItem>
                        <SelectItem value="Kgs">Kgs (Kilograms)</SelectItem>
                        <SelectItem value="Tons">Tons</SelectItem>
                        <SelectItem value="Meters">Meters</SelectItem>
                        <SelectItem value="Pieces">Pieces</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Pricing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="price">Total Price *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      className="pl-8"
                    />
                  </div>
                  {formData.quantity && formData.price && (
                    <p className="text-sm text-gray-600">
                      Rate per {formData.unit}: ₹{(parseFloat(formData.price) / parseFloat(formData.quantity) || 0).toFixed(2)}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Dispatch Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Truck className="w-5 h-5 mr-2" />
                    Dispatch Management
                  </div>
                  <Dialog open={dispatchDialogOpen} onOpenChange={setDispatchDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Dispatch
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Dispatch</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="dispatchDate">Dispatch Date</Label>
                          <Input
                            id="dispatchDate"
                            type="date"
                            value={dispatchForm.dispatchDate}
                            onChange={(e) => setDispatchForm(prev => ({ ...prev, dispatchDate: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="dispatchNumber">Dispatch Number</Label>
                          <Input
                            id="dispatchNumber"
                            placeholder="e.g., DISP-007"
                            value={dispatchForm.dispatchNumber}
                            onChange={(e) => setDispatchForm(prev => ({ ...prev, dispatchNumber: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="dispatchQuantity">
                            Quantity ({order.unit}) - Max: {formatQuantity(order.balance, order.unit)}
                          </Label>
                          <Input
                            id="dispatchQuantity"
                            type="number"
                            step="0.01"
                            max={order.balance}
                            placeholder="Enter quantity"
                            value={dispatchForm.quantity}
                            onChange={(e) => setDispatchForm(prev => ({ ...prev, quantity: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="dispatchWeight">
                            Total Weight (Kgs)
                          </Label>
                          <Input
                            id="dispatchWeight"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Enter total weight in Kgs"
                            value={dispatchForm.weightInKgs}
                            onChange={(e) => setDispatchForm(prev => ({ ...prev, weightInKgs: e.target.value }))}
                          />
                        </div>
                        <div className="flex space-x-3 pt-4">
                          <Button onClick={handleAddDispatch} className="flex-1">
                            Add Dispatch
                          </Button>
                          <Button variant="outline" onClick={() => setDispatchDialogOpen(false)} className="flex-1">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {order.dispatches.length === 0 ? (
                  <div className="text-center py-8">
                    <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Dispatches Yet</h3>
                    <p className="text-gray-600">Add the first dispatch for this order.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Dispatch Number</TableHead>
                          <TableHead className="text-right">Quantity</TableHead>
                          <TableHead className="text-right">Weight (Kgs)</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {order.dispatches.map((dispatch) => (
                          <TableRow key={dispatch.id}>
                            <TableCell>{formatDate(dispatch.dispatchDate)}</TableCell>
                            <TableCell className="font-medium">{dispatch.dispatchNumber}</TableCell>
                            <TableCell className="text-right">
                              {formatQuantity(dispatch.quantity, dispatch.unit)}
                            </TableCell>
                            <TableCell className="text-right">
                              {dispatch.weightInKgs ? formatWeight(dispatch.weightInKgs) : 'N/A'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end space-x-1">
                                <Button variant="ghost" size="sm" title="Edit Dispatch">
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleDeleteDispatch(dispatch.id)}
                                  title="Delete Dispatch"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary Sidebar */}
          <div className="space-y-6">
            {/* Order Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    order.status === 'Complete' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {order.status}
                  </span>
                </div>
                <Separator />
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Quantity</span>
                    <span className="font-medium">{formatQuantity(order.quantity, order.unit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dispatched</span>
                    <span className="text-green-600 font-medium">
                      {formatQuantity(order.quantity - order.balance, order.unit)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Balance</span>
                    <span className="text-yellow-600 font-medium">
                      {formatQuantity(order.balance, order.unit)}
                    </span>
                  </div>
                </div>
                <Separator />
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Value</span>
                    <span className="font-medium">{formatCurrency(order.price)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pending Value</span>
                    <span className="text-yellow-600 font-medium">
                      {formatCurrency((order.price * order.balance) / order.quantity)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to={`/orders/${order.id}`} className="block">
                  <Button variant="outline" className="w-full justify-start">
                    View Order Details
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setDispatchDialogOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Dispatch
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  Export Order
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditOrder;